import { useMutation, useQueryClient, UseMutationOptions } from '@tanstack/react-query';
import { updateUser, type User } from '@/api/entities/users';
import { ExtractFnReturnType } from '@/lib/react-query';
import { useCognitoAuth } from '@/hooks/useCognitoAuth';
import { GraphQLRequestError } from '@/api/graphql';

type UpdateUserParams = {
  id: string;
  input: Partial<User>;
};

type QueryFnType = typeof updateUser;

type UseUpdateUserOptions = {
  config?: Omit<
    UseMutationOptions<ExtractFnReturnType<QueryFnType>, GraphQLRequestError, UpdateUserParams>,
    'mutationFn'
  >;
};

export const useUpdateUser = ({ config }: UseUpdateUserOptions = {}) => {
  const queryClient = useQueryClient();
  const { getAccessToken } = useCognitoAuth();

  return useMutation<ExtractFnReturnType<QueryFnType>, GraphQLRequestError, UpdateUserParams>({
    ...config,
    mutationFn: async ({ id, input }: UpdateUserParams) => {
      return updateUser(id, input, getAccessToken);
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users', variables.id] });
      config?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};
